import { startTransition, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { format } from 'date-fns';
import { Box, Button, Container, Grid, Stack, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import Page from '../../components/Page';
import Iconify from '../../components/Iconify';
import AdminLogoutButton from '../../components/AdminLogoutButton';
import { PATH_APP } from '../../routes/paths';
import { Link as RouterLink } from 'react-router-dom';
import {
  claimCardRedemptionOrder,
  completeCardRedemptionOrder,
  disputeCardRedemptionOrder,
  fetchCardRedemptionOrderDetails,
  fetchCardRedemptionOrders,
  findCardRedemptionOrder,
  rejectCardRedemptionOrder,
} from '../../services/cardRedemption';
import useAuth from '../../hooks/useAuth';
import useCardRedemptionSocket from '../../hooks/useCardRedemptionSocket';
import { useDispatch, useSelector } from '../../redux/store';
import {
  clearSearchMode,
  fetchOrdersFailure,
  fetchOrdersStart,
  fetchOrdersSuccess,
  prependOrder,
  setFilters,
  setSearchMode,
  setSelectedOrderId,
  setWebsocketStatus,
  updateOrder,
} from '../../redux/slices/cardRedemption';
import CardRedemptionOrderListPane from '../../sections/card-redemption/CardRedemptionOrderListPane';
import CardRedemptionOrderDetailView from '../../sections/card-redemption/CardRedemptionOrderDetailView';
import CardRateEnquiryDialog from '../../sections/card-redemption/CardRateEnquiryDialog';
import CompleteOrderDialog from '../../sections/card-redemption/CompleteOrderDialog';
import DisputeOrderDialog from '../../sections/card-redemption/DisputeOrderDialog';
import RejectOrderDialog from '../../sections/card-redemption/RejectOrderDialog';
import NotificationTray from '../../sections/card-redemption/order-notification-tray/NotificationTray';
import useNotificationTone from '../../sections/card-redemption/order-notification-tray/useNotificationTone';

function serializeFilters(filters) {
  return {
    ...filters,
    start_date: filters.start_date ? format(filters.start_date, 'yyyy-MM-dd') : undefined,
    end_date: filters.end_date ? format(filters.end_date, 'yyyy-MM-dd') : undefined,
  };
}

function extractRealtimeOrder(payload) {
  const candidates = [payload, payload?.data, payload?.order, payload?.data?.order].filter(
    (candidate) => candidate && typeof candidate === 'object' && !Array.isArray(candidate)
  );

  return candidates.find((candidate) => typeof candidate.order_id === 'string') || null;
}

const LIST_LOADING_STATES = {
  idle: 'idle',
  initial: 'initial',
  refresh: 'refresh',
  pagination: 'pagination',
  filters: 'filters',
  search: 'search',
};

const VIEWED_TRAY_ITEM_TTL = 4000;

export default function CardRedemptionApp() {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { currentAdmin, user } = useAuth();
  const { items, filters, loading, pagination, searchMode, searchQuery, selectedOrderId } = useSelector(
    (state) => state.cardRedemption
  );

  const [searchInput, setSearchInput] = useState('');
  const [detailsState, setDetailsState] = useState({ loading: false, data: null, error: null });
  const [activeDialog, setActiveDialog] = useState(null);
  const [actionLoading, setActionLoading] = useState('');
  const [openedOrderId, setOpenedOrderId] = useState(null);
  const [isRateEnquiryOpen, setIsRateEnquiryOpen] = useState(false);
  const [listLoadingState, setListLoadingState] = useState(LIST_LOADING_STATES.initial);
  const [notificationTrayItems, setNotificationTrayItems] = useState([]);
  const [isNotificationTrayCollapsed, setIsNotificationTrayCollapsed] = useState(false);
  const initialFiltersRef = useRef(filters);
  const detailsRequestIdRef = useRef(0);
  const detailsCacheRef = useRef({});
  const listRequestIdRef = useRef(0);
  const viewedTrayTimersRef = useRef({});
  const itemsRef = useRef(items);
  const playNotificationTone = useNotificationTone();

  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  const orderMap = useMemo(
    () =>
      items.reduce((accumulator, item) => {
        accumulator[item.order_id] = item;
        return accumulator;
      }, {}),
    [items]
  );
  const openedOrder = openedOrderId ? orderMap[openedOrderId] || null : null;

  const loadOrders = useCallback(
    async (nextFilters, nextLoadingState = LIST_LOADING_STATES.refresh) => {
      const requestId = listRequestIdRef.current + 1;
      listRequestIdRef.current = requestId;
      setListLoadingState(nextLoadingState);
      dispatch(fetchOrdersStart());

      try {
        const response = await fetchCardRedemptionOrders(serializeFilters(nextFilters));
        dispatch(fetchOrdersSuccess(response.data || {}));
      } catch (error) {
        dispatch(fetchOrdersFailure(error.message || error.toString()));
        enqueueSnackbar(error.message || 'Unable to fetch orders', { variant: 'error' });
      } finally {
        if (listRequestIdRef.current === requestId) {
          setListLoadingState(LIST_LOADING_STATES.idle);
        }
      }
    },
    [dispatch, enqueueSnackbar]
  );

  const loadOrderDetails = useCallback(
    async (orderId) => {
      if (!orderId) {
        setDetailsState({ loading: false, data: null, error: null });
        return;
      }

      const cachedDetails = detailsCacheRef.current[orderId] || null;
      const requestId = detailsRequestIdRef.current + 1;
      detailsRequestIdRef.current = requestId;

      setDetailsState({ loading: true, data: cachedDetails, error: null });

      try {
        const response = await fetchCardRedemptionOrderDetails(orderId);
        if (detailsRequestIdRef.current !== requestId) {
          return;
        }

        const nextDetails = response.data || null;

        if (nextDetails) {
          detailsCacheRef.current[orderId] = nextDetails;
        }

        setDetailsState({ loading: false, data: nextDetails, error: null });
      } catch (error) {
        if (detailsRequestIdRef.current !== requestId) {
          return;
        }

        setDetailsState({
          loading: false,
          data: cachedDetails,
          error: error.message || error.toString(),
        });
        enqueueSnackbar(error.message || 'Unable to fetch order details', { variant: 'error' });
      }
    },
    [enqueueSnackbar]
  );

  useEffect(() => {
    loadOrders(initialFiltersRef.current, LIST_LOADING_STATES.initial);
  }, [loadOrders]);

  useEffect(() => {
    loadOrderDetails(openedOrderId);
  }, [loadOrderDetails, openedOrderId]);

  useEffect(() => {
    if (openedOrderId && !items.some((item) => item.order_id === openedOrderId)) {
      setOpenedOrderId(null);
    }
  }, [items, openedOrderId]);

  const dismissTrayItem = useCallback((orderId) => {
    const timerId = viewedTrayTimersRef.current[orderId];

    if (timerId) {
      window.clearTimeout(timerId);
      delete viewedTrayTimersRef.current[orderId];
    }

    setNotificationTrayItems((current) => current.filter((item) => item.order_id !== orderId));
  }, []);

  const markTrayItemViewed = useCallback(
    (orderId) => {
      let shouldScheduleDismiss = false;

      setNotificationTrayItems((current) =>
        current.map((item) => {
          if (item.order_id !== orderId || item.viewed) {
            return item;
          }

          shouldScheduleDismiss = true;
          return { ...item, viewed: true };
        })
      );

      if (!shouldScheduleDismiss) {
        return;
      }

      const existingTimer = viewedTrayTimersRef.current[orderId];

      if (existingTimer) {
        window.clearTimeout(existingTimer);
      }

      viewedTrayTimersRef.current[orderId] = window.setTimeout(() => {
        dismissTrayItem(orderId);
      }, VIEWED_TRAY_ITEM_TTL);
    },
    [dismissTrayItem]
  );

  useEffect(
    () => () => {
      Object.values(viewedTrayTimersRef.current).forEach((timerId) => window.clearTimeout(timerId));
    },
    []
  );

  const handleRealtimeMessage = useCallback(
    (payload, meta) => {
      const nextOrder = extractRealtimeOrder(payload);

      if (!nextOrder) {
        return;
      }

      const exists = itemsRef.current.some((item) => item.order_id === nextOrder.order_id);
      dispatch(exists ? updateOrder(nextOrder) : prependOrder(nextOrder));

      if (meta?.eventName === 'order.created') {
        startTransition(() => {
          setNotificationTrayItems((current) => {
            const existing = current.find((item) => item.order_id === nextOrder.order_id);
            const nextTrayItem = {
              ...existing,
              ...nextOrder,
              order_id: nextOrder.order_id,
              received_at: new Date().toISOString(),
              viewed: false,
            };

            return [nextTrayItem, ...current.filter((item) => item.order_id !== nextOrder.order_id)];
          });
        });
        playNotificationTone();
      }

      if (openedOrderId === nextOrder.order_id) {
        delete detailsCacheRef.current[nextOrder.order_id];
        loadOrderDetails(nextOrder.order_id);
      }
    },
    [dispatch, loadOrderDetails, openedOrderId, playNotificationTone]
  );

  const websocketStatus = useCardRedemptionSocket(handleRealtimeMessage);

  useEffect(() => {
    dispatch(setWebsocketStatus(websocketStatus));
  }, [dispatch, websocketStatus]);

  const handleFiltersChange = useCallback(
    (field, value) => {
      dispatch(setFilters({ [field]: value }));
    },
    [dispatch]
  );

  const handleApplyFilters = async () => {
    const nextFilters = { ...filters, page: 1 };
    dispatch(setFilters({ page: 1 }));
    dispatch(clearSearchMode());
    setOpenedOrderId(null);
    await loadOrders(nextFilters, LIST_LOADING_STATES.filters);
  };

  const handlePageChange = async (page) => {
    const nextFilters = { ...filters, page };
    dispatch(setFilters({ page }));
    await loadOrders(nextFilters, LIST_LOADING_STATES.pagination);
  };

  const handleSearch = async () => {
    const query = searchInput.trim();

    if (!query) {
      dispatch(clearSearchMode());
      setOpenedOrderId(null);
      await loadOrders(filters, LIST_LOADING_STATES.refresh);
      return;
    }

    const requestId = listRequestIdRef.current + 1;
    listRequestIdRef.current = requestId;
    setListLoadingState(LIST_LOADING_STATES.search);
    dispatch(fetchOrdersStart());

    try {
      const response = await findCardRedemptionOrder(query);
      dispatch(
        fetchOrdersSuccess({
          data: response.data || [],
          current_page: 1,
          next_page: null,
          total_pages: 1,
          total_size: (response.data || []).length,
          previous_page: null,
          page_size: (response.data || []).length || filters.page_size,
        })
      );
      dispatch(setSearchMode({ searchMode: true, query }));
    } catch (error) {
      dispatch(fetchOrdersFailure(error.message || error.toString()));
      enqueueSnackbar(error.message || 'Order not found', { variant: 'error' });
    } finally {
      if (listRequestIdRef.current === requestId) {
        setListLoadingState(LIST_LOADING_STATES.idle);
      }
    }
  };

  const handleClearSearch = async () => {
    setSearchInput('');
    dispatch(clearSearchMode());
    setOpenedOrderId(null);
    await loadOrders(filters, LIST_LOADING_STATES.refresh);
  };

  const handleSelectOrder = useCallback(
    (orderId) => {
      dispatch(setSelectedOrderId(orderId));
      setOpenedOrderId(orderId);
      markTrayItemViewed(orderId);
    },
    [dispatch, markTrayItemViewed]
  );

  const handleRefreshOrders = useCallback(async () => {
    if (searchMode && searchQuery) {
      setSearchInput(searchQuery);

      const requestId = listRequestIdRef.current + 1;
      listRequestIdRef.current = requestId;
      setListLoadingState(LIST_LOADING_STATES.refresh);
      dispatch(fetchOrdersStart());

      try {
        const response = await findCardRedemptionOrder(searchQuery);
        dispatch(
          fetchOrdersSuccess({
            data: response.data || [],
            current_page: 1,
            next_page: null,
            total_pages: 1,
            total_size: (response.data || []).length,
            previous_page: null,
            page_size: (response.data || []).length || filters.page_size,
          })
        );
      } catch (error) {
        dispatch(fetchOrdersFailure(error.message || error.toString()));
        enqueueSnackbar(error.message || 'Unable to refresh search result', { variant: 'error' });
      } finally {
        if (listRequestIdRef.current === requestId) {
          setListLoadingState(LIST_LOADING_STATES.idle);
        }
      }

      return;
    }

    await loadOrders(filters, LIST_LOADING_STATES.refresh);
  }, [dispatch, enqueueSnackbar, filters, loadOrders, searchMode, searchQuery]);

  const handleCloseOpenedOrder = useCallback(() => {
    setOpenedOrderId(null);
  }, []);

  const handleOpenCompleteDialog = useCallback(() => {
    setActiveDialog('complete');
  }, []);

  const handleOpenDisputeDialog = useCallback(() => {
    setActiveDialog('dispute');
  }, []);

  const handleOpenRejectDialog = useCallback(() => {
    setActiveDialog('reject');
  }, []);

  const handleClaim = async () => {
    if (!openedOrder) {
      return;
    }

    try {
      setActionLoading('claim');
      const response = await claimCardRedemptionOrder({
        order_id: openedOrder.order_id,
        remark: 'Order claimed for review',
      });

      dispatch(
        updateOrder({
          ...openedOrder,
          staff_id: currentAdmin?.staff_id || user?.staff_id || '',
          staff_name:
            [user?.firstname || user?.first_name, user?.lastname || user?.last_name].filter(Boolean).join(' ') || '',
        })
      );
      enqueueSnackbar(response.message, { variant: response.status ? 'success' : 'error' });
      await loadOrderDetails(openedOrder.order_id);
    } catch (error) {
      enqueueSnackbar(error.message || 'Unable to claim order', { variant: 'error' });
    } finally {
      setActionLoading('');
    }
  };

  const handleComplete = async ({
    delay_minutes,
    files,
    settlement_type,
    silent_notification,
    total_amount,
  }) => {
    if (!openedOrder) {
      return;
    }

    try {
      setActionLoading('complete');
      const formData = new FormData();
      formData.append('order_id', openedOrder.order_id);
      formData.append('total_amount', total_amount);
      formData.append('settlement_type', settlement_type || 'offline_payment');
      formData.append('silent_notification', silent_notification);
      formData.append('delay_minutes', delay_minutes);

      files.forEach((file, index) => {
        formData.append(`userfile${index}`, file);
      });

      const response = await completeCardRedemptionOrder(formData);
      dispatch(
        updateOrder({ ...openedOrder, status: 'completed', staff_id: currentAdmin?.staff_id || openedOrder.staff_id })
      );
      enqueueSnackbar(response.message, { variant: response.status ? 'success' : 'error' });
      setActiveDialog(null);
      await loadOrderDetails(openedOrder.order_id);
    } catch (error) {
      enqueueSnackbar(error.message || 'Unable to complete order', { variant: 'error' });
    } finally {
      setActionLoading('');
    }
  };

  const handleReject = async (values) => {
    if (!openedOrder) {
      return;
    }

    try {
      setActionLoading('reject');
      const response = await rejectCardRedemptionOrder({
        order_id: openedOrder.order_id,
        ...values,
      });
      dispatch(
        updateOrder({ ...openedOrder, status: 'rejected', staff_id: currentAdmin?.staff_id || openedOrder.staff_id })
      );
      enqueueSnackbar(response.message, { variant: response.status ? 'success' : 'error' });
      setActiveDialog(null);
      await loadOrderDetails(openedOrder.order_id);
    } catch (error) {
      enqueueSnackbar(error.message || 'Unable to reject order', { variant: 'error' });
    } finally {
      setActionLoading('');
    }
  };

  const handleDispute = async (values) => {
    if (!openedOrder) {
      return;
    }

    try {
      setActionLoading('dispute');
      const response = await disputeCardRedemptionOrder({
        order_id: openedOrder.order_id,
        ...values,
      });
      dispatch(
        updateOrder({
          ...openedOrder,
          amount: values.amount,
          settlement_amount: values.settlement_amount,
          number_of_cards: values.number_of_cards,
          staff_id: currentAdmin?.staff_id || openedOrder.staff_id,
        })
      );
      enqueueSnackbar(response.message, { variant: response.status ? 'success' : 'error' });
      setActiveDialog(null);
      await loadOrderDetails(openedOrder.order_id);
    } catch (error) {
      enqueueSnackbar(error.message || 'Unable to dispute order', { variant: 'error' });
    } finally {
      setActionLoading('');
    }
  };

  return (
    <Page title="Card Redemption">
      <Box
        sx={{
          minHeight: '100vh',
          background:
            'linear-gradient(180deg, rgba(15,23,42,0.03) 0%, rgba(255,255,255,1) 22%), linear-gradient(135deg, rgba(34,197,94,0.08), rgba(37,99,235,0.04))',
          py: 3,
        }}
      >
        <Container maxWidth={false}>
          <Stack spacing={3}>
            {/* <Card> */}
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', md: 'center' }}
              spacing={2}
            >
              <Box>
                <Typography variant="overline" color="success.main" sx={{ letterSpacing: 1.4 }}>
                  Card Redemption App
                </Typography>
                <Typography variant="h4" sx={{ mb: 0.5 }}>
                  Workspace for giftcard redemption
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Claim, review, and resolve card redemption orders from one focused surface.
                </Typography>
              </Box>

              <Stack direction="row" spacing={1.5}>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => setIsRateEnquiryOpen(true)}
                  startIcon={<Iconify icon="solar:calculator-linear" />}
                >
                  Rate enquiry
                </Button>
                <Button
                  component={RouterLink}
                  to={PATH_APP.selector}
                  variant="outlined"
                  startIcon={<Iconify icon="solar:widget-5-bold" />}
                >
                  App selector
                </Button>
                <AdminLogoutButton variant="contained" color="primary" />
              </Stack>
            </Stack>
            {/* </Card> */}

            <Grid container spacing={2.5} sx={{ minHeight: 'calc(100vh - 180px)' }}>
              <Grid item xs={12} lg={4} xl={4}>
                <CardRedemptionOrderListPane
                  filters={filters}
                  filterLoading={listLoadingState === LIST_LOADING_STATES.filters}
                  loading={loading}
                  loadingState={listLoadingState}
                  onApplyFilters={handleApplyFilters}
                  onClearSearch={handleClearSearch}
                  onFiltersChange={handleFiltersChange}
                  onPageChange={handlePageChange}
                  onRefresh={handleRefreshOrders}
                  onSearch={handleSearch}
                  onSearchInputChange={setSearchInput}
                  onSelectOrder={handleSelectOrder}
                  openedOrderId={openedOrderId}
                  orders={items}
                  pagination={pagination}
                  searchInput={searchInput}
                  searchMode={searchMode}
                  searchQuery={searchQuery}
                  selectedOrderId={selectedOrderId}
                  websocketStatus={websocketStatus}
                />
              </Grid>

              <Grid item xs={12} lg={8} xl={8}>
                <CardRedemptionOrderDetailView
                  actionLoading={actionLoading}
                  details={detailsState.data}
                  detailsLoading={detailsState.loading}
                  onClaim={handleClaim}
                  onCloseOrder={handleCloseOpenedOrder}
                  onOpenComplete={handleOpenCompleteDialog}
                  onOpenDispute={handleOpenDisputeDialog}
                  onOpenReject={handleOpenRejectDialog}
                  order={openedOrder}
                />
              </Grid>
            </Grid>
          </Stack>
        </Container>

        <NotificationTray
          collapsed={isNotificationTrayCollapsed}
          items={notificationTrayItems}
          onDismissItem={dismissTrayItem}
          onOpenItem={handleSelectOrder}
          onToggleCollapsed={() => setIsNotificationTrayCollapsed((current) => !current)}
          selectedOrderId={openedOrderId}
        />
      </Box>

      <CompleteOrderDialog
        open={activeDialog === 'complete'}
        onClose={() => setActiveDialog(null)}
        onSubmit={handleComplete}
        loading={actionLoading === 'complete'}
        order={openedOrder}
      />

      <RejectOrderDialog
        open={activeDialog === 'reject'}
        onClose={() => setActiveDialog(null)}
        onSubmit={handleReject}
        loading={actionLoading === 'reject'}
      />

      <DisputeOrderDialog
        open={activeDialog === 'dispute'}
        onClose={() => setActiveDialog(null)}
        onSubmit={handleDispute}
        loading={actionLoading === 'dispute'}
        order={openedOrder}
      />

      <CardRateEnquiryDialog open={isRateEnquiryOpen} onClose={() => setIsRateEnquiryOpen(false)} />
    </Page>
  );
}
